import axios from 'axios';
import { getToken } from './auth';
import {
  CLASSES_ACTIVITY_TYPE,
  SPORTS_ACTIVITY_TYPE,
} from '../shared/activityCatalog';
const utilitiesURL = process.env.REACT_APP_URL + '/utilities'
const API_URL = process.env.REACT_APP_URL + '/icapps'
const API_URL_CENTER = process.env.REACT_APP_URL + '/centros'
const ACTIVITY_TYPES_URL = `${utilitiesURL}/tipoactividad`;
const GEOIP_API_URL = `https://api.ipregistry.co/?key=${process.env.REACT_APP_IPREGISTRY_API_KEY}`;
const headers = { 'Content-Type': 'application/json' };

const fallbackClassActivities = [
  { id: 'class-yoga', name: 'Yoga' },
  { id: 'class-pilates', name: 'Pilates' },
  { id: 'class-crossfit', name: 'Crossfit' },
  { id: 'class-rumba', name: 'Rumba' },
];

const normalizeText = (value = '') =>
  String(value)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const getSportsByActivityType = (sports = [], legacyActivityTypeId) =>
  sports.filter((sport) =>
    legacyActivityTypeId
      ? String(sport.idtipoactividad) === String(legacyActivityTypeId)
      : true,
  );

const getSportsActivities = (sports = []) => {
  const sportActivities = getSportsByActivityType(sports, 1);
  const defaultLegacySportId = sportActivities[0]?.iddeporte || 1;

  return sportActivities.length
    ? sportActivities.map((sport, index) => ({
        id: `${SPORTS_ACTIVITY_TYPE}-${index}`,
        name: sport.nombre,
        slug: `${sport.iddeporte}`,
        legacySportId: sport.iddeporte,
      }))
    : [
        {
          id: defaultLegacySportId,
          name: 'Futbol',
          slug: `${defaultLegacySportId}`,
          legacySportId: defaultLegacySportId,
        },
      ];
};

const getCanonicalActivityTypeId = (activityType = {}) => {
  const rawId = activityType.id ?? activityType.idtipoactividad ?? activityType.slug;
  const rawName = activityType.name || activityType.nombre || '';
  const normalizedId = normalizeText(rawId);
  const normalizedName = normalizeText(rawName);

  if (
    normalizedId === SPORTS_ACTIVITY_TYPE ||
    normalizedName.includes('deporte') ||
    rawId === 1
  ) {
    return SPORTS_ACTIVITY_TYPE;
  }

  if (
    normalizedId === CLASSES_ACTIVITY_TYPE ||
    normalizedName.includes('clase') ||
    rawId === 2
  ) {
    return CLASSES_ACTIVITY_TYPE;
  }

  return normalizedId || String(rawId || '');
};

const getActivityTypeName = (activityType = {}, canonicalId) => {
  const rawName = activityType.name || activityType.nombre;

  if (rawName) {
    return rawName;
  }

  if (canonicalId === SPORTS_ACTIVITY_TYPE) {
    return 'Deportes';
  }

  if (canonicalId === CLASSES_ACTIVITY_TYPE) {
    return 'Clases';
  }

  return 'Actividad';
};

const getFallbackActivitiesByType = (canonicalId, sports = []) => {
  if (canonicalId === SPORTS_ACTIVITY_TYPE) {
    return getSportsActivities(sports);
  }

  if (canonicalId === CLASSES_ACTIVITY_TYPE) {
    const classSports = getSportsByActivityType(sports, 2);

    return classSports.length
      ? normalizeActivities(classSports, CLASSES_ACTIVITY_TYPE)
      : fallbackClassActivities.map((activity) => ({
          ...activity,
          slug: activity.id,
        }));
  }

  return [];
};

const normalizeActivities = (activities = [], activityTypeId, sports = []) =>
  activities.map((activity, index) => ({
    id:
      activity.id ||
      activity.idactividad ||
      activity.slug ||
      `${activityTypeId}-${index}`,
    name: activity.name || activity.nombre,
    slug: activity.slug || activity.id || activity.idactividad,
    legacySportId:
      activity.legacySportId ||
      activity.iddeporte ||
      sports[index]?.iddeporte ||
      (String(activity.id || activity.idactividad || '').match(/^\d+$/)
        ? activity.id || activity.idactividad
        : undefined),
  }));

const getImageUrl = image =>
  typeof image === 'string'
    ? image
    : image?.url || image?.original || image?.thumbnail || image?.imagen || '';

const normalizeClassRecord = (classRecord = {}, options = {}) => {
  const imageUrls = Array.isArray(classRecord.imagenes)
    ? classRecord.imagenes.map(getImageUrl).filter(Boolean)
    : [];
  const mainImage =
    getImageUrl(classRecord.imagen) ||
    getImageUrl(classRecord.imagenclase) ||
    getImageUrl(classRecord.logosede) ||
    imageUrls[0];
  const price = classRecord.monto ?? classRecord.precio;
  const availableSlots =
    classRecord.cuposDisponibles ??
    classRecord.cuposdisponibles ??
    classRecord.cupos_libres ??
    classRecord.cuposlibres ??
    classRecord.cuposLibres ??
    classRecord.cupos ??
    classRecord.disponibles ??
    classRecord.disponibilidad;
  const totalSlots =
    classRecord.cuposTotales ??
    classRecord.cupostotales ??
    classRecord.cupos_totales ??
    classRecord.cupostotal ??
    classRecord.cuposTotal ??
    classRecord.capacidad ??
    classRecord.capacidadtotal ??
    classRecord.capacidadTotal;

  return {
    ...classRecord,
    activityType: CLASSES_ACTIVITY_TYPE,
    idclase: classRecord.idclase || classRecord.id,
    idcentro: classRecord.idcentro || options.idcentro,
    idcancha: classRecord.idcancha || classRecord.idclase || classRecord.id,
    nombrecancha: classRecord.nombrecancha || classRecord.nombre || 'Clase',
    monto: price !== undefined && price !== null ? parseFloat(price) : undefined,
    precio: price,
    direccion: classRecord.direccion || classRecord.direccionsede,
    imagen: mainImage,
    imagenes: imageUrls.length
      ? imageUrls.map(url => ({ url }))
      : mainImage
        ? [{ url: mainImage }]
        : [],
    atributos: classRecord.atributos || classRecord.servicios || [],
    proximaSesion: classRecord.proxima_sesion || classRecord.proximaSesion,
    cuposDisponibles: availableSlots,
    cuposTotales: totalSlots,
    clasificacionsiglas: classRecord.clasificacionsiglas || 'Clase'
  };
};

const getLegacyActivityTypeId = (activityType = {}) => {
  if (activityType.legacyActivityTypeId) {
    return activityType.legacyActivityTypeId;
  }

  if (activityType.id === SPORTS_ACTIVITY_TYPE) {
    return 1;
  }

  if (activityType.id === CLASSES_ACTIVITY_TYPE) {
    return 2;
  }

  return activityType.idtipoactividad ?? activityType.id ?? activityType.slug;
};

const buildFallbackActivityCatalog = (sports = []) => {
  const classSports = getSportsByActivityType(sports, 2);
  const defaultLegacySportId = classSports[0]?.iddeporte || 1;
  const sportsActivities = getSportsActivities(sports);

  return [
    {
      id: SPORTS_ACTIVITY_TYPE,
      name: 'Deportes',
      slug: SPORTS_ACTIVITY_TYPE,
      legacyActivityTypeId: 1,
      activities: sportsActivities,
    },
    {
      id: CLASSES_ACTIVITY_TYPE,
      name: 'Clases',
      slug: CLASSES_ACTIVITY_TYPE,
      legacyActivityTypeId: 2,
      activities: fallbackClassActivities.map((activity) => ({
        ...activity,
        slug: activity.id,
        legacySportId: defaultLegacySportId,
      })),
    },
  ];
};

const getSportFilter = (filters = {}) => {
  if (filters.sport !== undefined && filters.sport !== null && filters.sport !== '' && filters.sport !== 0) {
    return filters.sport;
  }

  const activity = String(filters.activity || '');
  const legacySport = activity.match(/^\d+$/) ? activity : null;

  return legacySport || undefined;
};

export const getGeolocationViaIP = async () => {
  try {
    const request = await axios.get(GEOIP_API_URL);
    const {
      data: { location },
    } = request;
    return {
      result: 200,
      geodata: {
        latitude: location.latitude,
        longitude: location.longitude,
        country: location.country.name,
        countryCode: location.country.code,
        city: location.city,
      },
    };
  } catch (error) {
  }
};

export const getCountries = async () => {
  try {
    const destination = `${utilitiesURL}/paises`;
    const connection = await axios({
      method: 'GET',
      url: destination,
      headers: headers,
    });
    const {
      data: { data, cod },
    } = connection;
    switch (cod) {
      case 1000:
        return { cod, data };
      default:
        return { cod };
    }
  } catch (error) {
  }
};

export const getCities = async (countryId) => {
  try {
    const destination = `${utilitiesURL}/ciudades?country=${countryId}`;
    const connection = await axios({
      method: 'GET',
      url: destination,
      headers: headers,
    });
    const {
      data: { data, cod },
    } = connection;
    switch (cod) {
      case 1000:
        return { cod, data };
      default:
        return { cod };
    }
  } catch (error) {
  }
};

export const getSports = async (idtipoactividad) => {
  try {
    const destination = idtipoactividad
      ? `${utilitiesURL}/deportes?idtipoactividad=${idtipoactividad}`
      : `${utilitiesURL}/deportes`;
    const connection = await axios({
      method: 'GET',
      url: destination,
      headers: headers,
    });
    const {
      data: { data, cod },
    } = connection;
    switch (cod) {
      case 1000:
        return { cod, data };
      default:
        return { cod };
    }
  } catch (error) {
  }
};

export const getActivitiesByActivityType = async (
  legacyActivityTypeId,
  activityTypeId
) => {
  try {
    const destination = `${utilitiesURL}/deportes?idtipoactividad=${legacyActivityTypeId}`;
    const connection = await axios({
      method: 'GET',
      url: destination,
      headers: headers,
    });
    const {
      data: { data, cod },
    } = connection;

    switch (cod) {
      case 1000:
        return {
          cod,
          data: normalizeActivities(data, activityTypeId || legacyActivityTypeId),
        };
      default:
        return { cod };
    }
  } catch (error) {
  }
};

const getCatalogActivitiesByType = async (activityType = {}, sports = []) => {
  const canonicalId = getCanonicalActivityTypeId(activityType);
  const legacyActivityTypeId = getLegacyActivityTypeId(activityType);
  const response = await getActivitiesByActivityType(
    legacyActivityTypeId,
    canonicalId,
  );

  if (response?.cod === 1000 && response.data?.length) {
    return response.data;
  }

  return getFallbackActivitiesByType(canonicalId, sports);
};

export const getActivityCatalog = async () => {
  let sports = [];

  try {
    const sportsResponse = await getSports();
    if (sportsResponse?.cod === 1000) {
      sports = sportsResponse.data || [];
    }
  } catch (error) {
  }

  try {
    const connection = await axios({
      method: 'GET',
      url: ACTIVITY_TYPES_URL,
      headers: headers,
    });
    const {
      data: { data, cod },
    } = connection;

    switch (cod) {
      case 1000:
        return {
          cod,
          data: await Promise.all(
            (data || []).map(async (activityType) => {
              const canonicalId = getCanonicalActivityTypeId(activityType);
              const legacyActivityTypeId = getLegacyActivityTypeId(activityType);

              return {
                id: canonicalId,
                name: getActivityTypeName(activityType, canonicalId),
                slug: canonicalId,
                legacyActivityTypeId,
                activities: await getCatalogActivitiesByType(
                  activityType,
                  sports,
                ),
              };
            }),
          ),
        };
      default:
        return { cod, data: buildFallbackActivityCatalog(sports) };
    }
  } catch (error) {
    return { cod: 1000, data: buildFallbackActivityCatalog(sports) };
  }
};

export const getFields = async (
  dateOrFilters,
  time,
  sport,
  latitude,
  longitude,
  country
) => {
  try {
    const filters =
      typeof dateOrFilters === 'object'
        ? dateOrFilters
        : {
            date: dateOrFilters,
            time,
            sport,
            latitude,
            longitude,
            country,
          };
    const isClassesSearch =
      String(filters.activityType) === CLASSES_ACTIVITY_TYPE;
    const queryFilters = isClassesSearch
      ? {
          time: filters.time,
          sport: filters.sport,
          latitude: filters.latitude,
          longitude: filters.longitude,
        }
      : {
          date: filters.date,
          time: filters.time,
          sport: getSportFilter(filters),
          latitude: filters.latitude,
          longitude: filters.longitude,
          country: filters.country,
        };
    const searchParams = new URLSearchParams();

    Object.entries(queryFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value);
      }
    });

    const destination = `${API_URL}/${isClassesSearch ? 'clases' : 'canchas'}?${searchParams.toString()}`;
    const connection = await axios({
      method: 'GET',
      url: destination,
      headers: headers,
    });
    const {
      data: { data, cod },
    } = connection;
    const responseData = isClassesSearch
      ? (data || []).map(normalizeClassRecord)
      : data;
    switch (cod) {
      case 1000:
        return { cod, data: responseData };
      default:
        return { cod };
    }
  } catch (error) {
  }
};

export const getField = async (idcancha) => {
  try {
    const destination = `${API_URL}/canchas/${idcancha}`;
    const connection = await axios({
      method: 'GET',
      url: destination,
      headers: headers,
    });
    const {
      data: { data, cod },
    } = connection;
    switch (cod) {
      case 1000:
        return { cod, data };
      default:
        return { cod };
    }
  } catch (error) {
  }
};

export const getClass = async (idcentro, idclase) => {
  try {
    const destination = `${API_URL}/centros/${idcentro}/clases/${idclase}`;
    const connection = await axios({
      method: 'GET',
      url: destination,
      headers: headers,
    });
    const {
      data: { data, cod },
    } = connection;
    switch (cod) {
      case 1000:
        return { cod, data: normalizeClassRecord(data) };
      default:
        return { cod };
    }
  } catch (error) {
  }
};

export const getClassById = async (idclase) => {
  try {
    const destination = `${API_URL}/clases/${idclase}`;
    const connection = await axios({
      method: 'GET',
      url: destination,
      headers: headers,
    });
    const {
      data: { data, cod },
    } = connection;
    switch (cod) {
      case 1000:
        return { cod, data: normalizeClassRecord(data) };
      default:
        return { cod };
    }
  } catch (error) {
  }
};

export const getClassSchedules = async (idclase, fecha) => {
  try {
    const destination = `${API_URL}/clases/${idclase}/horarios?fecha=${fecha}`;
    const connection = await axios({
      method: 'GET',
      url: destination,
      headers: headers,
    });
    const {
      data: { data, cod },
    } = connection;
    switch (cod) {
      case 1000:
        return { cod, data };
      default:
        return { cod };
    }
  } catch (error) {
  }
};

export const getRecommendedClasses = async (idcentro, idclase) => {
  try {
    const destination = `${API_URL}/centros/${idcentro}/clases/${idclase}/recommendations`;
    const connection = await axios({
      method: 'GET',
      url: destination,
      headers: headers,
    });
    const {
      data: { data, cod },
    } = connection;
    switch (cod) {
      case 1000:
        return {
          cod,
          data: (data || []).map(classRecord =>
            normalizeClassRecord(classRecord, { idcentro })
          ),
        };
      default:
        return { cod };
    }
  } catch (error) {
  }
};

export const getFieldTarifas = async (idcancha, fecha, country) => {
  try {
    const destination = `${API_URL}/canchas/${idcancha}/tarifas?fecha=${fecha}&country=${country}`;
    const connection = await axios({
      method: 'GET',
      url: destination,
      headers: headers,
    });
    const {
      data: { data, cod },
    } = connection;
    switch (cod) {
      case 1000:
        return { cod, data };
      default:
        return { cod };
    }
  } catch (error) {
  }
};

export const getSedes = async (sport, latitude, longitude, name) => {
  try {
    let destination;
    const sportParam = (sport !== undefined && sport !== null && sport !== '') ? `sport=${sport}&` : '';
    if (name) {
      destination = `${API_URL}/sedes?${sportParam}latitude=${latitude}&longitude=${longitude}&name=${name}`;
    } else {
      destination = `${API_URL}/sedes?${sportParam}latitude=${latitude}&longitude=${longitude}`;
    }
    const connection = await axios({
      method: 'GET',
      url: destination,
      headers: headers,
    });
    const {
      data: { data, cod },
    } = connection;
    switch (cod) {
      case 1000:
        return { cod, data };
      default:
        return { cod };
    }
  } catch (error) {
  }
};

export const getSedesOnCentro = async (idcentro, latitude, longitude) => {
  try {
    let destination = `${API_URL}/centros/${idcentro}/sedes?latitude=${latitude}&longitude=${longitude}`;
    const connection = await axios({
      method: 'GET',
      url: destination,
      headers: headers,
    });
    const {
      data: { data, cod },
    } = connection;
    switch (cod) {
      case 1000:
        return { cod, data };
      default:
        return { cod };
    }
  } catch (error) {
  }
};
export const getCentro = async (idcentro) => {
  try {
    let destination = `${API_URL_CENTER}/${idcentro}`;
    const connection = await axios({
      method: 'GET',
      url: destination,
      headers: headers,
    });
    const {
      data: { data, cod },
    } = connection;
    switch (cod) {
      case 1000:
        return { cod, data };
      default:
        return { cod };
    }
  } catch (error) {
  }
};

export const getFieldsOnSede = async (idsede) => {
  try {
    let destination = `${API_URL}/sedes/${idsede}/fields`;
    const connection = await axios({
      method: 'GET',
      url: destination,
      headers: headers,
    });
    const {
      data: { data, cod },
    } = connection;
    switch (cod) {
      case 1000:
        return { cod, data };
      default:
        return { cod };
    }
  } catch (error) {
  }
};

export const getDocuments = async (country) => {
  try {
    let destination = `${utilitiesURL}/documentos?country=${country}`;
    const connection = await axios({
      method: 'GET',
      url: destination,
      headers: headers,
    });
    const {
      data: { data, cod },
    } = connection;
    switch (cod) {
      case 1000:
        return { cod, data };
      default:
        return { cod };
    }
  } catch (error) {
  }
};

export const createBooking = async (timestamprsvp, timestamprsvpend, tarifa, idsede, idcentro, idcancha, cupon, reservasinpago, reservaconpago) => {
  try {
    const customOrigin = process.env.REACT_APP_CENTER_KEY;
    let destination;

    if (customOrigin) {
      destination = `${API_URL}/centros/${idcentro}/canchas/${idcancha}/rsvp?origen=${customOrigin}`;
      //destination = `${API_URL}/centros/16/canchas/2/rsvp?origen=${customOrigin}`;
    } else {
      destination = `${API_URL}/centros/${idcentro}/canchas/${idcancha}/rsvp`;
      //destination = `${API_URL}/centros/16/canchas/2/rsvp`;
    }

    const bodyData = {
      timestamprsvp,
      timestamprsvpend,
      tarifa: parseFloat(tarifa),
      idsede,
      cupon: cupon ? cupon : '',
      ...!!reservasinpago && {reservasinpago: true},
      ...!!reservaconpago && {reservaconpago: true}
    };
    const token = getToken();
    const connection = await axios({
      method: 'POST',
      url: destination,
      headers: { ...headers, Authorization: `Bearer ${token}` },
      data: bodyData,
    });

    const {
      data: { cod, data },
    } = connection;

    switch (cod) {
      case 1000:
        return { cod, data };
      default:
        return { cod };
    }
  } catch (error) {
  }
};

export const addUserToSession = async (
  idhistoricorsvp,
  idusuario,
  cupos,
  cupon,
  reservaconpago = true
) => {
  try {
    const destination = `${API_URL}/sesiones/${idhistoricorsvp}/usuarios`;
    const cuposValue = parseInt(cupos, 10) || 1;
    const bodyData = {
      idusuario,
      cupos: Math.max(1, cuposValue),
      cupon: cupon ? cupon : '',
      reservaconpago,
    };
    const token = getToken();
    const connection = await axios({
      method: 'POST',
      url: destination,
      headers: { ...headers, Authorization: `Bearer ${token}` },
      data: bodyData,
    });

    const {
      data: {
        cod,
        data,
        urlPago,
        urlpago,
        paymentUrl,
        url,
      },
    } = connection;
    const responseData = data || { urlPago, urlpago, paymentUrl, url };

    switch (cod) {
      case 1000:
        return { cod, data: responseData };
      default:
        return { cod, error: connection.data?.error || connection.data?.respuesta };
    }
  } catch (error) {
    return {
      cod: error.response?.data?.cod || 500,
      error: error.response?.data?.error || error.response?.data?.respuesta || error.message
    };
  }
};

///// metodo de infoCoupon /////
export const getInfoCoupon = async (coupon) => {
  try {
    let destination = `${utilitiesURL}/coupons/${coupon}`;
    const connection = await axios({
      method: 'GET',
      url: destination,
      headers: headers,
    });
    const {
      data: { data, cod },
    } = connection;
    switch (cod) {
      case 1000:
        return { cod, data };
      default:
        return { cod };
    }
  } catch (error) {
  }
};
/////////////////////////////////

// Respuesta book positiva
/*
{
  "cod": 1000,
  "respuesta": "Solicitud procesada de forma exitosa",
  "data": {
      "timestamprsvp": "2022-02-25T23:00:00.000Z",
      "idcancha": "2",
      "tarifahorarsvp": 50000,
      "pagadoencentro": "0.00",
      "porpagarencentro": 50000,
      "utilidadicancha": 10000,
      "utilidadcentro": 40000,
      "timestamprsvpend": "2022-02-26T00:00:00.000Z",
      "creadopor": 1,
      "idorigenrsvp": 3,
      "idporcentajeic": 3,
      "idusuario": 8271,
      "fechacancelado": null,
      "canceladopor": null,
      "modificadopor": null,
      "idhistoricorsvp": 37349,
      "montopagoapp": "0.00",
      "montototaldscto": "0.00",
      "montodsctocentro": "0.00",
      "fechacreado": "2022-02-25T19:51:27.355Z",
      "jugado": false,
      "torneo": false,
      "cancelado": false,
      "facturar": false,
      "urlPago": "https://pagos.icancha.com?idprsvp=123734931"
  }
}
*/

// reservada
/* {
  "cod": 1000,
  "respuesta": "Solicitud procesada de forma exitosa",
  "data": {
      "reservada": true
  }
} */

export const getClassesByCenter = async (time, idcentro, idsede, idcancha) => {
  try {
    const searchParams = new URLSearchParams();
    if (time) searchParams.append('time', time);
    if (idcentro) searchParams.append('idcentro', idcentro);
    if (idsede) searchParams.append('idsede', idsede);
    if (idcancha) searchParams.append('idcancha', idcancha);

    const destination = `${API_URL}/clasesbycenter?${searchParams.toString()}`;
    const connection = await axios({
      method: 'GET',
      url: destination,
      headers: headers,
    });
    const {
      data: { data, cod },
    } = connection;
    switch (cod) {
      case 1000:
        return { cod, data: (data || []).map(normalizeClassRecord) };
      default:
        return { cod };
    }
  } catch (error) {
    console.error("Error getClassesByCenter:", error);
  }
};

export const getClassesByDateRange = async (filters = {}) => {
  try {
    const searchParams = new URLSearchParams();
    if (filters.time) searchParams.append('time', filters.time);
    if (filters.endtime) searchParams.append('endtime', filters.endtime);
    if (filters.idcentro) searchParams.append('idcentro', filters.idcentro);
    if (filters.idsede) searchParams.append('idsede', filters.idsede);
    if (filters.idcancha) searchParams.append('idcancha', filters.idcancha);
    if (filters.idclase) searchParams.append('idclase', filters.idclase);
    if (filters.sport) searchParams.append('sport', filters.sport);
    if (filters.latitude !== undefined && filters.latitude !== null && filters.latitude !== '') {
      searchParams.append('latitude', filters.latitude);
    }
    if (filters.longitude !== undefined && filters.longitude !== null && filters.longitude !== '') {
      searchParams.append('longitude', filters.longitude);
    }
    searchParams.append('page', filters.page ?? '0');
    searchParams.append('size', filters.size ?? '1000');

    const destination = `${API_URL}/clases-por-fecha?${searchParams.toString()}`;
    const connection = await axios({
      method: 'GET',
      url: destination,
      headers: headers,
    });
    const {
      data: { data, cod },
    } = connection;
    switch (cod) {
      case 1000:
        return { cod, data: Array.isArray(data) ? data : (data?.content || []) };
      default:
        return { cod, data: [] };
    }
  } catch (error) {
    console.error("Error getClassesByDateRange:", error);
    return { cod: 500, data: [] };
  }
};

